import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Copy, Check, X, ExternalLink, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate } from '../lib/raceUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'lgra.klhalf.strip.dismissed.v2';

/**
 * KLHalfPromoStrip — slim, dismissible partner-promo strip at the top of the
 * homepage. Data-driven: it fetches the currently PUBLISHED partner-promo race
 * from the race calendar and only renders when one exists (so it stays in sync
 * with admin approval — nothing shows until a promo race is published).
 */
const KLHalfPromoStrip = () => {
  const [promo, setPromo] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/public/race-calendar/promo`)
      .then((r) => { if (alive) setPromo(r.data?.promo || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (dismissed || !promo?.promo?.code) return null;

  const P = promo.promo;
  const registerUrl = promo.link || (promo.registrations?.[0]?.url);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(P.code);
      setCopied(true);
      toast.success(`Promo code ${P.code} copied`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — long-press to copy the code');
    }
  };

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
  };

  return (
    <div className="w-full bg-gradient-to-r from-[#EA580C] via-[#F97316] to-[#FB923C] text-white" data-testid="klhalf-promo-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="inline-flex items-center gap-1.5 shrink-0 rounded-full bg-white/20 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest">
          <Tag size={11} strokeWidth={2.75} /> {P.partner_label || 'LGRA Partner Promo'}
        </span>

        <div className="flex-1 min-w-[200px] text-sm leading-tight">
          <span className="font-black">{promo.event}</span>
          <span className="opacity-90"> • {fmtDate(promo.start_date)}{promo.venue ? ` • ${promo.venue}` : ''}</span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={copyCode}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white/15 hover:bg-white/25 border border-white/40 px-2.5 py-1 text-xs font-black tracking-wider transition-colors"
            data-testid="klhalf-strip-copy-code"
            aria-label={`Copy promo code ${P.code}`}
          >
            {copied ? <Check size={13} /> : <Copy size={13} />} {P.code}
          </button>
          {registerUrl && (
            <a
              href={registerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-white text-[#C2410C] hover:bg-[#FFF7ED] px-3 py-1 text-xs font-black uppercase tracking-widest transition-colors"
              data-testid="klhalf-strip-register"
            >
              Register <ExternalLink size={12} />
            </a>
          )}
          <Link
            to="/race-calendar"
            className="hidden sm:inline text-[11px] font-bold underline underline-offset-2 hover:no-underline"
            data-testid="klhalf-strip-calendar-link"
          >
            Race calendar
          </Link>
          <button
            type="button"
            onClick={dismiss}
            className="ml-1 rounded p-1 hover:bg-white/20 transition-colors"
            aria-label="Dismiss promo"
            data-testid="klhalf-strip-dismiss"
          >
            <X size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default KLHalfPromoStrip;
